import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useGenerateReconReport } from "../../api/orders";
import useNotify from "../../hooks/useNotify";
import { useUserStore } from "../../stores/userStore";
import { mono } from "../../theme/colors";
import DateField from "../atoms/DateField";
import Text from "../atoms/Text";

export interface GenerateReportDialogProps {
  open: boolean;
  onClose: () => void;
}

function GenerateReportDialog(props: GenerateReportDialogProps) {
  const { onClose, open } = props;
  const prevMonth = dayjs(new Date()).subtract(1, "month");
  const lastDatePrevMonth = prevMonth.endOf("month");
  const firstDatePrevMonth = prevMonth.startOf("month");
  const formMethods = useForm<{
    fromDate: string;
    toDate: string;
  }>({
    defaultValues: {
      fromDate: firstDatePrevMonth.format("DD/MM/YYYY"),
      toDate: lastDatePrevMonth.format("DD/MM/YYYY"),
    },
  });
  const { mutate, isLoading } = useGenerateReconReport();
  const userEmail = useUserStore(state => state.user?.email);
  const { setNotifyError, setNotifySuccess } = useNotify();

  useEffect(() => {
    // Reset the date when dialog closes or opens
    formMethods.reset();
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  function handleGenerate() {
    const values = formMethods.getValues();
    if (userEmail) {
      mutate(
        {
          ...values,
          userIdEmail: userEmail,
        },
        {
          onSuccess: () => {
            onClose();
            setNotifySuccess(
              "Report will be generated and sent to your email inbox",
            );
          },
        },
      );
    } else {
      setNotifyError("No user logged in, please log in again.");
    }
  }

  return (
    <Dialog onClose={handleClose} open={open} maxWidth={"md"}>
      <DialogTitle
        sx={{
          bgcolor: mono.light.grey,
        }}
      >
        Generate Reconciliation Report
      </DialogTitle>
      <DialogContent>
        <FormProvider {...formMethods}>
          <Stack direction={"column"} gap={3}>
            <Stack direction={"column"}>
              <Text variant="body_small" my={1}>
                Please select the start and end date for which you want to
                generate the reconciliation report for.
              </Text>
              <Stack direction={"row"} gap={3} my={1}>
                <DateField name="fromDate" label="Start Date" />
                <DateField
                  name="toDate"
                  label="End Date"
                  dateNotBefore="startDate"
                />
              </Stack>
              <Text variant="body_small">Note:</Text>
              <Text variant="body_small">
                1. The report will only include Reward Category which is on
                demand.
              </Text>
              <Text variant="body_small">
                2. The report will only show successful transactions on Omne end
                during specific time period. Failed transaction won't be
                included as they won't be charged.
              </Text>
              <Text variant="body_small">
                3. Only enable downloading data of latest 60 days.
              </Text>
              <Text variant="body_small">
                4. After clicking on generate, reports will be sent through
                email when it's ready
              </Text>
            </Stack>
            <Stack
              direction={"row"}
              display={"flex"}
              justifyContent={"center"}
              gap={1}
            >
              <Button
                variant="secondary"
                onClick={handleClose}
                sx={{
                  width: "10em",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                sx={{
                  width: "10em",
                }}
                disabled={isLoading}
                onClick={handleGenerate}
                startIcon={isLoading && <CircularProgress size={20} />}
              >
                Generate
              </Button>
            </Stack>
          </Stack>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

export default function GenerateReportButton() {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Button sx={{ height: "40px" }} variant="secondary" onClick={handleOpen}>
        Generate Reconciliation Report
      </Button>
      <GenerateReportDialog open={open} onClose={handleClose} />
    </>
  );
}
